import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FileSpreadsheet, ChevronDown } from "lucide-react";
import { PageHeader } from "@/components/AdminLayout";
import { api, apiError } from "@/lib/api";
import { fmtMoney, CURRENCIES, chargeToTemplate } from "@/lib/format";
import { ChargeDetailSheet } from "@/components/ChargeDetailSheet";
import { PreviewDialog } from "@/components/PreviewDialog";
import { useAuth } from "@/context/AuthContext";

const iso = (d) => d.toISOString().slice(0, 10);
const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return iso(d); };
const RANGES = [["7", "7 días"], ["30", "30 días"], ["90", "90 días"]];
const dm = (s) => (s ? `${s.slice(8, 10)}/${s.slice(5, 7)}` : "");

const Stat = ({ label, value, testid, sub, tone, onClick, active }) => {
  const refund = tone === "refund";
  const ring = active ? (refund ? "ring-2 ring-violet-400" : "ring-2 ring-[#2F5BFF]") : "";
  const Tag = onClick ? "button" : "div";
  return (
    <Tag type={onClick ? "button" : undefined} onClick={onClick} data-testid={onClick ? `${testid}-card` : undefined} aria-expanded={onClick ? !!active : undefined}
      className={`rounded-2xl border p-5 text-left ${refund ? "border-violet-200 bg-violet-50/60" : "border-slate-200 bg-white"} ${ring} ${onClick ? "transition-shadow hover:shadow-md" : ""}`}>
      <p className={`flex items-center justify-between text-xs font-semibold uppercase tracking-wider ${refund ? "text-violet-700" : "text-slate-500"}`}>
        {label}{onClick && <ChevronDown size={16} className={`transition-transform ${active ? "rotate-180" : ""}`} />}
      </p>
      <p className={`mt-2 font-heading text-2xl font-semibold sm:text-3xl ${refund ? "text-violet-800" : "text-slate-900"}`} data-testid={testid}>{value}</p>
      {(sub || onClick) && <p className="mt-1 text-xs text-slate-500" data-testid={`${testid}-sub`}>{sub || "Toca para ver el detalle"}</p>}
    </Tag>
  );
};

const fmtAt = (s) => (s ? new Date(s).toLocaleString("es-MX", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

const SalesList = ({ kind, rows, currency, onOpen }) => {
  const refund = kind === "refund";
  return (
    <div className={`overflow-hidden rounded-2xl border bg-white ${refund ? "border-violet-200" : "border-slate-200"}`} data-testid={`sales-list-${kind}`}>
      <p className="border-b border-slate-100 px-5 py-4 font-semibold text-slate-900">{refund ? "Reembolsos del periodo" : "Pagos confirmados del periodo"} <span className="text-sm font-normal text-slate-400">· {rows.length}</span></p>
      {!rows.length ? <p className="px-5 py-6 text-sm text-slate-400">{refund ? "Sin reembolsos en este periodo" : "Sin pagos en este periodo"}</p> : (
        <div className="max-h-[420px] overflow-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="sticky top-0 bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>{["Huésped", "Plantilla / hotel", "Cajero", refund ? "Reembolsado" : "Pagado", refund ? "Motivo" : null, "Monto"].filter(Boolean).map((h) => <th key={h} className="px-5 py-2.5 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} onClick={() => onOpen(r.id)} title="Ver detalle del cobro" className="cursor-pointer border-t border-slate-100 transition-colors hover:bg-slate-50" data-testid={`sales-list-${kind}-row`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{r.guest_name || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{r.template}{r.property_name && <span className="block text-xs text-slate-400">{r.property_name}</span>}</td>
                  <td className="px-5 py-3 text-slate-600">{r.cashier || "—"}</td>
                  <td className="whitespace-nowrap px-5 py-3 text-slate-600">{fmtAt(r.at)}</td>
                  {refund && <td className="max-w-[220px] px-5 py-3 text-slate-600">{r.reason || "—"}</td>}
                  <td className={`whitespace-nowrap px-5 py-3 text-right font-mono font-semibold ${refund ? "text-violet-700" : "text-slate-900"}`}>{refund ? "−" : ""}{fmtMoney(r.amount_cents, currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const Ranking = ({ title, rows, currency, testid }) => {
  const max = Math.max(1, ...rows.map((r) => r.cents));
  return (
    <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
      <p className="mb-4 font-semibold text-slate-900">{title}</p>
      {!rows.length && <p className="text-sm text-slate-400">Sin pagos en este periodo</p>}
      <div className="space-y-4">
        {rows.map((r) => (
          <div key={r.key} data-testid={`${testid}-row`}>
            <div className="flex items-baseline justify-between gap-3 text-sm">
              <span className="min-w-0 text-slate-700"><span className="block truncate">{r.label}</span>{r.template && <span className="block truncate text-xs text-slate-400">{r.template}</span>}</span>
              <span className="shrink-0 font-mono font-semibold text-slate-900">{fmtMoney(r.cents, currency)} <span className="text-xs font-normal text-slate-400">· {r.count}</span></span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-[#2F5BFF] transition-[width] duration-500" style={{ width: `${(r.cents / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Pills = ({ items, value, onChange, testid }) => (
  <div className="inline-flex max-w-full flex-wrap rounded-2xl border border-slate-200 bg-white p-1">
    {items.map(([v, l]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testid}-${v}`}
        className={`rounded-full px-3.5 py-1.5 text-sm transition-colors ${value === v ? "bg-[#0D1117] font-semibold text-white" : "text-slate-500 hover:text-slate-900"}`}>{l}</button>
    ))}
  </div>
);

export default function Sales() {
  const { user } = useAuth();
  const [currency, setCurrency] = useState("USD");
  const [from, setFrom] = useState(daysAgo(29));
  const [to, setTo] = useState(iso(new Date()));
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);
  const [hotels, setHotels] = useState([]);
  const [preview, setPreview] = useState(null);
  const [reload, setReload] = useState(0);
  const [groups, setGroups] = useState([]);
  const [groupId, setGroupId] = useState("");
  useEffect(() => {
    if (user?.role === "admin") api.get("/groups").then((r) => setGroups(r.data));
    else if (["manager", "agent"].includes(user?.role)) api.get("/my-groups").then((r) => setGroups(r.data));
  }, [user?.role]);
  useEffect(() => { api.get("/hotels").then((r) => setHotels(r.data)); }, []);
  const showPreview = (c) => { const h = hotels.find((x) => x.slug === c.hotel_slug); if (h) setPreview({ hotel: h, data: chargeToTemplate(c) }); };
  const toggle = (k) => setOpen((o) => (o === k ? null : k));

  useEffect(() => {
    api.get("/sales", { params: { currency, date_from: from, date_to: to, group_id: groupId || undefined } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e).message));
  }, [currency, from, to, reload, groupId]);

  const setRange = (n) => { setFrom(daysAgo(Number(n) - 1)); setTo(iso(new Date())); };
  const exportXlsx = async () => {
    try {
      const r = await api.get("/sales/export.xlsx", { params: { currency, date_from: from, date_to: to, group_id: groupId || undefined }, responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `ventas-${currency}-${from}-a-${to}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("No se pudo descargar el Excel"); }
  };
  const groupName = groups.find((g) => g.id === data?.group_id)?.name;
  const scope = user?.role === "admin" ? (groupName ? `Grupo ${groupName}` : "Todos los cobros") : ["manager", "agent"].includes(user?.role) ? (groupName ? `Grupo ${groupName}` : "Tus cobros") : "Tus cobros";
  const groupItems = user?.role === "admin" ? [["all", "Todos los grupos"], ...groups.map((g) => [g.id, g.name])] : groups.map((g) => [g.id, g.name]);
  const showGroups = user?.role === "admin" ? groups.length > 0 : groups.length > 1;
  const days = data?.by_day.length || 0;

  return (
    <>
      <PageHeader title="Panel de ventas" subtitle={`${scope} · pagos confirmados por día, plantilla, hotel, usuario y grupo.`}
        actions={<Button variant="outline" onClick={exportXlsx} data-testid="sales-export-xlsx-btn"><FileSpreadsheet size={16} className="mr-2" />Descargar Excel</Button>} />
      <div className="mb-6 flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 xl:flex-row xl:flex-wrap xl:items-center">
        <div className="flex flex-wrap gap-3">
          <Pills items={CURRENCIES.map((c) => [c, c])} value={currency} onChange={setCurrency} testid="sales-currency" />
          <Pills items={RANGES} value={null} onChange={setRange} testid="sales-range" />
        </div>
        <div className="flex min-w-0 items-center gap-2 xl:ml-auto">
          <Input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className="min-w-0 flex-1 sm:w-40 sm:flex-none" data-testid="sales-date-from" />
          <span className="text-slate-400">a</span>
          <Input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className="min-w-0 flex-1 sm:w-40 sm:flex-none" data-testid="sales-date-to" />
        </div>
        {showGroups && (
          <div className="flex w-full flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-500">Grupo</span>
            <Pills items={groupItems} value={groupId || (user?.role === "admin" ? "all" : data?.group_id)} onChange={(v) => setGroupId(v === "all" ? "" : v)} testid="sales-group" />
          </div>
        )}
      </div>
      {data && (
        <div className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Stat label="Total cobrado" value={fmtMoney(data.total_cents, currency)} testid="sales-total" sub="Ya sin reembolsos" />
            <Stat label="Total reembolsado" value={fmtMoney(data.refunded_cents || 0, currency)} testid="sales-refunded" tone="refund"
              sub={`${data.refunded_count || 0} ${data.refunded_count === 1 ? "reembolso" : "reembolsos"} en el periodo · toca para ver`}
              onClick={() => toggle("refund")} active={open === "refund"} />
            <Stat label="Pagos confirmados" value={data.count} testid="sales-count" onClick={() => toggle("paid")} active={open === "paid"} />
            <Stat label="Promedio por día con ventas" value={fmtMoney(days ? Math.round(data.total_cents / days) : 0, currency)} testid="sales-avg" />
          </div>
          {open === "paid" && <SalesList kind="paid" rows={data.paid_list || []} currency={currency} onOpen={setDetail} />}
          {open === "refund" && <SalesList kind="refund" rows={data.refund_list || []} currency={currency} onOpen={setDetail} />}
          <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="sales-by-day">
            <p className="mb-4 font-semibold text-slate-900">Cobrado por día</p>
            {data.by_day.length ? (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.by_day.map((d) => ({ ...d, day: dm(d.key), amount: d.cents / 100 }))}>
                    <CartesianGrid vertical={false} stroke="#EEF0F4" />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748B" }} axisLine={false} tickLine={false} width={60} />
                    <Tooltip formatter={(v, n, p) => [`${fmtMoney(p.payload.cents, currency)} · ${p.payload.count} pagos`, "Cobrado"]} cursor={{ fill: "#F1F5F9" }} />
                    <Bar dataKey="amount" fill="#2F5BFF" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : <p className="text-sm text-slate-400">Sin pagos en este periodo</p>}
          </div>
          <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-3">
            <Ranking title="Por plantilla" rows={data.by_hotel} currency={currency} testid="sales-by-hotel" />
            <Ranking title="Por cajero / usuario" rows={data.by_user} currency={currency} testid="sales-by-user" />
            <Ranking title="Por grupo" rows={data.by_group} currency={currency} testid="sales-by-group" />
            <Ranking title="Por hotel" rows={data.by_property || []} currency={currency} testid="sales-by-property" />
          </div>
        </div>
      )}
      <ChargeDetailSheet chargeId={detail} onOpenChange={(o) => { if (!o) setDetail(null); }} hotels={hotels} onChanged={() => setReload((k) => k + 1)} onPreview={showPreview} />
      <PreviewDialog open={!!preview} onOpenChange={() => setPreview(null)} hotel={preview?.hotel} data={preview?.data} />
    </>
  );
}
